import { pad2 } from '../format/pad2.ts';
import { parseServerInstant } from '../format/serverInstant.ts';

/**
 * The two conversions between a stored UTC instant and an `<input type="datetime-local">`'s own
 * value format (`YYYY-MM-DDTHH:mm`, always local, never `Z`-suffixed).
 *
 * Kept out of the form component and tested on their own: getting either direction wrong is a
 * booking silently landing an hour off, and that is exactly the kind of thing worth pinning down
 * with a test rather than eyeballing in a browser once.
 *
 * `toDatetimeLocalValue` parses its input with `parseServerInstant` (`../format/serverInstant.ts`),
 * not a bare `new Date(iso)` — this function's whole caller (`AppointmentFormDialog`'s reschedule
 * mode) feeds it a real `appointment.startsAt`/`endsAt` straight from the server, which is Postgres
 * `timestamptz` text (`"2026-08-18 15:00:00+00"`), not ISO 8601 — see `serverInstant.ts`'s own header
 * for why that distinction is not cosmetic. `fromDatetimeLocalValue` is the other direction and stays
 * on a plain `new Date(value)`: its input is the `datetime-local` control's *own* value, which is the
 * one non-`serverInstant.ts` case in this feature where ECMA-262's Date Time String Format actually
 * does guarantee local-time parsing for the exact `YYYY-MM-DDTHH:mm` shape the control produces.
 */

/** A UTC instant (as the server sends it) to the local value a `datetime-local` input expects. */
export function toDatetimeLocalValue(iso: string): string {
  const date = parseServerInstant(iso);
  const year = String(date.getFullYear());
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  const hours = pad2(date.getHours());
  const minutes = pad2(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/** A `datetime-local` input's value, interpreted in the browser's own local timezone (exactly how
 * the input itself interprets what it displays), to the `Z`-suffixed UTC instant
 * `createAppointmentRequestSchema`/`updateAppointmentRequestSchema` require. `''` (an empty or
 * not-yet-filled-in field) has no valid instant, so it returns `null` rather than `Invalid Date`'s
 * `toISOString()` throw. */
export function fromDatetimeLocalValue(value: string): string | null {
  if (value === '') return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}
